import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { inr, fmtDateTime, downloadFile } from "@/api";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { User, CalendarCheck, CalendarOff, FileDown, Pencil } from "lucide-react";

const CELL = { Present: "bg-success", Absent: "bg-danger", "Half-day": "bg-warning", Leave: "bg-info", Holiday: "bg-primary", "Week-off": "bg-muted-foreground", Overtime: "bg-accent" };

export default function MyHR() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [att, setAtt] = useState([]);
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);

  useEffect(() => {
    api.get("/attendance/my", { params: { month } }).then((r) => setAtt(r.data)).catch(() => {});
    api.get("/leaves/balance").then((r) => setBalance(r.data)).catch(() => {});
    api.get("/leaves").then((r) => setLeaves((r.data || []).filter((l) => l.employee_id === user.id))).catch(() => {});
  }, [month, user.id]);

  const present = att.filter((a) => a.status === "Present").length;
  const late = att.filter((a) => a.late).length;
  const requestCorrection = async (record) => {
    const reason = window.prompt(`Reason for correcting ${record.date}:`);
    if (!reason?.trim()) return;
    const checkIn = window.prompt("Requested check-in time (ISO timestamp, blank to leave unchanged):", record.check_in || "");
    if (checkIn === null) return;
    const checkOut = window.prompt("Requested check-out time (ISO timestamp, blank to leave unchanged):", record.check_out || "");
    if (checkOut === null) return;
    const requestedStatus = window.prompt("Requested status (blank to leave unchanged):", record.status || "");
    if (requestedStatus === null) return;
    try {
      await api.post("/attendance/corrections", {
        attendance_id: record.id, reason,
        check_in: checkIn || undefined,
        check_out: checkOut || undefined,
        status: requestedStatus || undefined,
      });
      toast.success("Correction request sent to the Owner");
    } catch (error) { toast.error(error.response?.data?.detail || "Correction request failed"); }
  };

  return (
    <div className="space-y-5" data-testid="myhr-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">My HR</h1>
          <p className="text-sm text-muted-foreground">Your profile, attendance, leaves & payslips</p>
        </div>
        <div className="flex gap-2 items-center">
          <input type="month" data-testid="myhr-month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-md border bg-background px-3 text-sm" />
          <Button variant="outline" data-testid="myhr-payslip" onClick={() => downloadFile(`/payroll/payslip.pdf?user_id=${user.id}&month=${month}`, `payslip-${month}.pdf`).then(() => toast.success("Payslip downloaded")).catch(() => toast.error("No salary data"))}>
            <FileDown size={15} className="mr-1.5" /> Payslip
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5 rounded-2xl" data-testid="myhr-profile">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2 text-sm"><User size={15} /> My Profile</h3>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center font-heading font-bold text-primary text-lg">{user.name?.[0]}</div>
            <div>
              <div className="font-semibold">{user.name}</div>
              <div className="text-xs text-muted-foreground">{user.email}</div>
              <div className="text-xs text-muted-foreground font-mono">Login ID: {user.login_id || "—"}</div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-lg bg-muted/50 px-3 py-2"><div className="text-[10px] uppercase text-muted-foreground">Department</div>{user.department || "—"}</div>
            <div className="rounded-lg bg-muted/50 px-3 py-2"><div className="text-[10px] uppercase text-muted-foreground">Joined</div>{user.joining_date || "—"}</div>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl" data-testid="myhr-attendance">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2 text-sm"><CalendarCheck size={15} /> My Attendance — {month}</h3>
          <div className="flex gap-4 mb-3">
            <div><div className="font-mono text-2xl font-bold text-success">{present}</div><div className="text-[10px] uppercase text-muted-foreground">Present</div></div>
            <div><div className="font-mono text-2xl font-bold text-warning-foreground">{late}</div><div className="text-[10px] uppercase text-muted-foreground">Late</div></div>
            <div><div className="font-mono text-2xl font-bold">{att.length}</div><div className="text-[10px] uppercase text-muted-foreground">Marked days</div></div>
          </div>
          <div className="space-y-1.5 max-h-44 overflow-y-auto">
            {att.map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs">
                <span className={`w-2.5 h-2.5 rounded ${CELL[a.status] || "bg-primary-tint"}`} />
                <span className="font-mono w-20">{a.date}</span>
                <span className="flex-1">{a.status}{a.late ? " · late" : ""}{a.early ? " · early out" : ""}</span>
                <span className="text-muted-foreground">{a.check_in ? fmtDateTime(a.check_in).split(", ")[1] : ""}{a.check_out ? ` → ${fmtDateTime(a.check_out).split(", ")[1]}` : ""}</span>
                <button type="button" aria-label={`Request correction for ${a.date}`} title="Request correction" onClick={() => requestCorrection(a)} className="rounded p-1 hover:bg-accent"><Pencil size={13} /></button>
              </div>
            ))}
            {!att.length && <div className="text-sm text-muted-foreground py-4 text-center">No attendance marked this month</div>}
          </div>
        </Card>

        <Card className="p-5 rounded-2xl" data-testid="myhr-leaves">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2 text-sm"><CalendarOff size={15} /> Leave Balance {balance?.year}</h3>
          {balance && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              {Object.entries(balance.quota).map(([t, q]) => (
                <div key={t} className="rounded-lg bg-muted/50 px-3 py-2">
                  <div className="text-[10px] uppercase text-muted-foreground">{t}</div>
                  <div className="font-mono font-bold">{q === null ? "∞" : `${balance.balance[t]} left`}</div>
                  <div className="text-[10px] text-muted-foreground">{balance.used[t] || 0} used{q !== null ? ` / ${q}` : ""}</div>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {leaves.map((l) => (
              <div key={l.id} className="flex items-center gap-2 text-xs border rounded-lg px-2.5 py-1.5">
                <span className="font-medium">{l.leave_type || "Casual"}</span>
                <span className="text-muted-foreground flex-1">{l.from_date} → {l.to_date}</span>
                <span className={`px-1.5 py-0.5 rounded-full border ${l.status === "Approved" ? "bg-success-tint text-success border-success/30" : l.status === "Rejected" ? "bg-danger-tint text-danger border-danger/30" : "bg-warning-tint text-warning border-warning/30"}`}>{l.status}</span>
              </div>
            ))}
            {!leaves.length && <div className="text-sm text-muted-foreground py-2 text-center">No leave requests yet</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
